import React, { useContext, useEffect, useMemo, useState } from 'react'

import { faCogs } from '@fortawesome/pro-solid-svg-icons'
import { Button, Modal, Tabs } from 'antd'
import { useSelector } from 'react-redux'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'

import { selectUser } from '../../../reducers/UserReducer'
import Loading from '../../../Components/shared/Loading'
import { getChildren } from '../../../utils/api/institutionOption'
import { InstitutionOptions } from '../../../utils/constants'
import { updateInstitutionOption } from '../../../utils/api/institution'
import { AUTOMATIC_ACCEPTANCE_PERMISSION, SCHEDULE_EDITION_PERMISSION } from '../../../utils/entities/SchoolPermission'
import SchoolPermissionTable from '../../../Components/institution/Modules/SchoolPermissionTable'
import SectionGroupsView from '../../../Components/institution/Modules/SectionGroups/SectionGroupsView'
import ModulesList from './ModulesList'
import { onSuccess, resolvePromises } from '../../../utils/apiHelper'
import { selectTranslate } from '../../../reducers/LocaleReducer/LocalReducer'
import { InstitutionContext } from '../../../Providers/InstitutionProvider'
import InternshipRequestMetadataTable from './InternshipRequestMetadataTable'

import '../../../assets/school-modules-settings.scss'

const TabPane = Tabs.TabPane

const SchoolModulesSettings = () => {
  const { institutionOptions } = useContext(InstitutionContext)

  const user = useSelector(selectUser)
  const t = useSelector(selectTranslate)

  const [options, setOptions] = useState([])
  const [availableOptions, setAvailableOptions] = useState([])
  const [loading, setLoading] = useState(true)
  const [displayModulesSetting, setDisplayModulesSetting] = useState(false)

  const moduleOption = useMemo(() => institutionOptions.find(io => io.optionType.type === InstitutionOptions.MODULE_SETTINGS), [institutionOptions])

  useEffect(() => {
    if (user.id && moduleOption) {
      setLoading(true)
      loadChildren(moduleOption, user)
    }
  }, [user.id, moduleOption])

  const loadChildren = (moduleOption, user) => {
    getChildren(user, moduleOption).then(json => {
      if (json && json.data) {
        const newAvailableOptions = json.data.filter(o => o.optionType.type !== InstitutionOptions.AUTO_ACCEPTATION_DAYS && !o.optionType.isHidden)

        setAvailableOptions(newAvailableOptions)
        setOptions(newAvailableOptions.filter(o => o.optionEnabled))
      }

      setLoading(false)
    })
  }

  const enableModules = enableModules => {
    const promiseStack = []

    setLoading(true)

    availableOptions.forEach(o => {
      o.institution = user.institutions[0]

      promiseStack.push(
        updateInstitutionOption(user, o, { optionEnabled: !!enableModules.find(id => id === o.id) })
      )
    })

    resolvePromises(promiseStack).then(() => {
      loadChildren(moduleOption, user)
      onSuccess(t('The modules has been successfully updated'))
    })
  }

  const getPane = type => {
    let pane = null

    if (type === InstitutionOptions.INTERNSHIPS_AUTO_ACCEPTATION) {
      pane = {
        Component: SchoolPermissionTable,
        props: { type: AUTOMATIC_ACCEPTANCE_PERMISSION }
      }
    } else if (type === InstitutionOptions.SCHEDULE_EDITION_BY_SCHOOLS) {
      pane = {
        Component: SchoolPermissionTable,
        props: { type: SCHEDULE_EDITION_PERMISSION }
      }
    } else if (type === InstitutionOptions.SECTION_GROUPS) {
      pane = {
        Component: SectionGroupsView
      }
    } else if (type === InstitutionOptions.INTERNSHIP_REQUEST_METADATA) {
      pane = {
        Component: InternshipRequestMetadataTable
      }
    }

    return pane
  }

  return (
    <div className={!loading && options.length === 0 ? 'no-modules-settings' : 'school-modules-settings'}>
      {loading && (
        <Loading size='10x' />
      )}
      {!loading && options.length === 0 && (
        <div className='no-modules-container'>
          <FontAwesomeIcon icon={faCogs} size='10x' color='#4F7CAC' />
          <ModulesList modules={availableOptions} onSave={enableModules} />
        </div>
      )}
      {!loading && options.length > 0 && (
        <>
          <Button onClick={() => setDisplayModulesSetting(true)}>
            <FontAwesomeIcon icon={faCogs} color='#4F7CAC' />
          </Button>
          <Tabs defaultActiveKey='0' animated={false}>
            {options.map((o, index) => {
              const Pane = getPane(o.optionType.type)

              return (
                <TabPane
                  key={index}
                  tab={t(o.optionType.type + '.tab_title')}
                >
                  {Pane === null && <span> UNKNOWN MODULE </span>}
                  {Pane !== null && <Pane.Component {...Pane.props} />}
                </TabPane>
              )
            })}
          </Tabs>
          <Modal visible={displayModulesSetting} footer={false} onCancel={() => setDisplayModulesSetting(false)}>
            <ModulesList
              emptySave
              modules={availableOptions}
              title='Select the modules to activate:'
              onSave={m => {
                setDisplayModulesSetting(false)
                enableModules(m)
              }}
            />
          </Modal>
        </>
      )}
    </div>
  )
}

export default SchoolModulesSettings
